/* eslint-disable no-prototype-builtins */
import SurveyTemplateDialog from '../../../components/SurveyTemplateDialog/SurveyTemplateDialog';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Module from '../../../../../../components/layout/Module';
import cleanTemplate from '../../../helpers/templateCleaner';
import Question from '../Question/Question';
import validateQuestions from '../../../helpers/questionValidator';
import MultiSelectDropdown from '../../../components/MultiSelectDropdown/MultiSelectDropdown';
import useFetchData from '../../../../../../hooks/useFetchData';
import {
  addQuestion,
  duplicateLastQuestion,
  updateQuestion,
  removeFormErrors,
  updateTemplate,
  setQuestionOptionErrors,
  resetState
} from '../../../../../../store/actions/dataEntryTemplateGeneratorActions';
import { toggleAlertModule, alertReset } from '../../../../../../store/actions/alert.actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  createTemplate,
  updateTemplateData,
  isTemplateTaken
} from '../../../../../../services/data-entry/templates.service';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Typography, Checkbox, FormControlLabel } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { selectAllOption } from '../../../constants/autocomplete';
import { debounce } from '../../../../../../utils/debounce';
import '../overriding.css';
import './TemplateBuilder.css';

const defaultQuestion = {
  id: 1,
  type: { value: 'shortanswer' },
  name: '',
  title: '',
  order: 1,
  isActive: true,
  required: true,
  options: []
};

const useDebouncedTemplateCheck = (dispatch) => {
  const debouncedFn = useRef(
    debounce((templateType, value, id) => {
      isTemplateTaken(templateType?._id, value, id).then((res) => {
        if (res?.data?.found) {
          dispatch(updateTemplate({ error: { title: 'This template title is already taken' } }));
        }
      });
    }, 300)
  );

  return debouncedFn.current;
};

const TemplateBuilder = ({ type = 'assessment', mode = 'adding' }) => {
  let qIndex = 1;
  const moduleTitle = type === 'assessment' ? 'Assessment' : 'Survey';
  const currentModule = 'templateassessmentgenerator';
  const moduletitle = `${moduleTitle} Generator`;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const saveRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const permissions = useSelector((state) => state?.session?.permissions?.templateassessmentgenerator || {});
  const generator = useSelector((state) => state?.dataEntrytemplateGenerator || {});
  const userRoles = useSelector((state) => state?.session?.user?.roles || []);
  const isMaestroUser = userRoles.find((role) => role?.id === 'Maestro');
  const { questions, error, title, yearLevels, roles, isEditable } = generator;
  const { data: dataRoles = [], loading: loadingRoles } = useFetchData('roles/standard');
  const { data: dataYears = [], loading: loadingYears } = useFetchData('setting/year');
  const { data: dataTemplates = [], loading: loadingTemplates } = useFetchData('setting/template-types');
  const { data: dataQuestions = [], loading: loadingQuestions } = useFetchData('setting/question-types');
  const [open, setOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const templateType = dataTemplates?.find(({ value }) => value === type);
  const isSurveyTemplate = templateType?.value === 'survey';
  const canUserEditTemplate = isMaestroUser || generator?.isEditable || mode === 'adding';

  let filteredTypes = dataQuestions?.filter((question) =>
    question?.templeteType?.includes(templateType?._id)
  );

  filteredTypes = !isSurveyTemplate
    ? filteredTypes?.map((question) => {
        const { variants = [], value } = question;
        let newVariants = [...variants];
        const hasImageVariant = variants?.find((item) => item?.value === 'image');

        if (value === 'singlechoice' && hasImageVariant) {
          newVariants = variants?.filter((item) => item?.value !== 'image');
        }
        return {
          ...question,
          variants: newVariants
        };
      })
    : filteredTypes;

  const filterDBYearLevels = dataYears?.map((year) => {
    return { id: year._id, label: year.name, value: year._id };
  });

  const filterDBRoles = dataRoles?.map((role) => {
    return { id: role._id, label: role.name, value: role._id };
  });

  const yearLevelsToRender = filterDBYearLevels?.length > 0 ? [selectAllOption, ...filterDBYearLevels] : [];

  const rolesToRender = filterDBRoles?.length > 0 ? [selectAllOption, ...filterDBRoles] : [];

  const selectedYearLevels = yearLevelsToRender?.filter((year) => {
    return yearLevels?.includes(year.value);
  });

  const selectedRoles = rolesToRender?.filter((role) => {
    return roles?.includes(role.value);
  });

  const sortedQuestions = [...questions].sort((a, b) => a.order - b.order);

  const alertSave = useSelector((state) => state?.alert?.save || false);

  const debouncedCheck = useDebouncedTemplateCheck(dispatch);

  const onAlert = (show, value, onSave, onDontSave) => {
    dispatch(toggleAlertModule(show, 'templates', value, onSave, onDontSave));
  };

  const onChangeHeader = (name, value) => {
    dispatch(removeFormErrors());

    if (name === 'yearLevels') {
      if (Array.isArray(value)) {
        const alreadyAllSelected = yearLevels?.includes('all');
        const includesAll = value?.includes('all');

        //Remove !== all
        if (alreadyAllSelected && includesAll) {
          const filteredList = value?.filter((item) => item !== 'all');
          dispatch(updateTemplate({ [name]: filteredList ?? [] }));
          return;
        }

        //Select all
        if (!alreadyAllSelected && includesAll) {
          const allYearLevels = filterDBYearLevels?.map((year) => year.value);
          dispatch(updateTemplate({ [name]: [...allYearLevels, 'all'] }));
          return;
        }

        //Add list specific
        if (!alreadyAllSelected && !includesAll) {
          if (value.length === filterDBYearLevels.length) {
            dispatch(updateTemplate({ [name]: [...value, 'all'] }));
          } else {
            dispatch(updateTemplate({ [name]: value }));
          }

          return;
        }

        //Remove all
        if (alreadyAllSelected && !includesAll) {
          dispatch(updateTemplate({ [name]: [] }));
          return;
        }
      }
    } else if (name === 'roles') {
      if (Array.isArray(value)) {
        const alreadyAllSelected = roles?.includes('all');
        const includesAll = value?.includes('all');

        //Remove !== all
        if (alreadyAllSelected && includesAll) {
          const filteredList = value?.filter((item) => item !== 'all');
          dispatch(updateTemplate({ [name]: filteredList ?? [] }));
          return;
        }

        //Select all
        if (!alreadyAllSelected && includesAll) {
          const allRoles = filterDBRoles?.map((year) => year.value);
          dispatch(updateTemplate({ [name]: [...allRoles, 'all'] }));
          return;
        }

        //Add list specific
        if (!alreadyAllSelected && !includesAll) {
          if (value.length === filterDBRoles.length) {
            dispatch(updateTemplate({ [name]: [...value, 'all'] }));
          } else {
            dispatch(updateTemplate({ [name]: value }));
          }

          return;
        }

        //Remove all
        if (alreadyAllSelected && !includesAll) {
          dispatch(updateTemplate({ [name]: [] }));
          return;
        }
      }
    } else {
      dispatch(updateTemplate({ [name]: value }));

      if (name === 'title' && value?.trim() !== '') {
        debouncedCheck(templateType, value, generator?._id);
      }
    }

    onChangeAlert();
  };

  //Add a new question to the form
  const handleAddNextQuestion = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    const newQuestion = {
      ...defaultQuestion,
      id: new Date().getTime(),
      order: questions?.length + 1,
      name: `question${questions.length + 1}`,
      isActive: true,
      error: {},
      required: true
    };

    dispatch(addQuestion(newQuestion));
    onChangeAlert();
  };

  //Duplicates last question from the form
  const handleOnDuplicateLastQuestion = (event) => {
    event.preventDefault();
    event.stopPropagation();

    const isFormValid = validateForm();

    if (!isFormValid) {
      return;
    }

    dispatch(duplicateLastQuestion());
  };

  const onChangeAlert = () => {
    if (alertSave && !alertSave.mode) {
      onAlert(false, mode, () => {
        if (saveRef?.current) {
          saveRef.current.click();
        }
      });
    }
  };

  const validateForm = useCallback(() => {
    dispatch(removeFormErrors());
    const error = validateQuestions(questions);

    if (Object.keys(error).length > 0) {
      if (error.hasOwnProperty('title')) {
        dispatch(
          updateQuestion({
            id: error?.questionId,
            error: { title: error?.title },
            isActive: true
          })
        );
      }

      if (error.hasOwnProperty('content')) {
        dispatch(
          updateQuestion({
            id: error?.questionId,
            error: { content: error?.content },
            isActive: true
          })
        );
      }

      if (error.hasOwnProperty('minRange')) {
        dispatch(
          updateQuestion({
            id: error?.questionId,
            error: { min: error?.minRange },
            isActive: true
          })
        );
      }

      if (error.hasOwnProperty('maxRange')) {
        dispatch(
          updateQuestion({
            id: error?.questionId,
            error: { max: error?.maxRange },
            isActive: true
          })
        );
      }

      if (error.hasOwnProperty('skipRange')) {
        dispatch(
          updateQuestion({
            id: error?.questionId,
            error: { skip: error?.skipRange },
            isActive: true
          })
        );
      }

      if (error.hasOwnProperty('questionId') && error.hasOwnProperty('options')) {
        dispatch(
          setQuestionOptionErrors({
            questionId: error?.questionId,
            options: error?.options,
            isActive: true
          })
        );
      }

      return false;
    }

    return true;
  }, [dispatch, questions]);

  const handleOnSaveTemplate = useCallback(async () => {
    try {
      if (!generator?.title) {
        dispatch(updateTemplate({ error: { title: 'Missing required title' } }));
        return;
      }

      if (generator?.error?.title) {
        return;
      }

      if (type === 'assessment') {
        if (!generator?.yearLevels || !generator?.yearLevels?.length) {
          dispatch(
            updateTemplate({
              error: { yearLevels: 'Missing required year level' }
            })
          );
          return;
        }
      }

      const isFormValid = validateForm();

      if (!isFormValid) {
        return;
      }
      //Cleaning up template before saving
      const cleanedTemplate = cleanTemplate(generator, templateType);
      const navigateTo = type === 'assessment' ? 'assessment-templates' : 'survey-templates';

      if (mode === 'editing') {
        const { data, error } = await updateTemplateData(cleanedTemplate?._id, cleanedTemplate);

        if (error || !data) {
          enqueueSnackbar('We could not save the template, please try again', {
            variant: 'error'
          });
        } else {
          dispatch(resetState());
          dispatch(alertReset());

          navigate(`/dashboard/data-entry/generator/${navigateTo}`);
          return;
        }
      } else {
        const { data, error } = await createTemplate(cleanedTemplate);

        if (error || !data) {
          enqueueSnackbar('We could not save the template, please try again', {
            variant: 'error'
          });
        } else {
          dispatch(resetState());
          dispatch(alertReset());

          navigate(`/dashboard/data-entry/generator/${navigateTo}`);
          return;
        }
      }
    } catch (error) {
      if (error?.name === 'ALREADY_TAKEN') {
        dispatch(updateTemplate({ error: { title: 'This template title is already taken' } }));
      } else {
        enqueueSnackbar('We could not save the template, please try again', {
          variant: 'error'
        });
      }
    }
  }, [dispatch, enqueueSnackbar, generator, navigate, mode, templateType, validateForm, type]);

  const handleOnPreviewTemplate = () => {
    if (type === 'survey') {
      setShowPreview(true);
    }
  };

  return (
    <Module>
      <Module.Header
        moduleName={currentModule}
        title={moduletitle}
        titleLink={
          mode === 'editing'
            ? `/dashboard/data-entry/generator/${type}-templates/edit/${generator?._id}`
            : `/dashboard/data-entry/generator/${type}-templates/add`
        }
      />
      <Module.Body>
        <Module.BodyHeader py='4' className='border-b border-b-2 border-primary'>
          <Box xs={2} className='flex items-center justify-start text-primary gap-2 max-w-150px min-w-150px'>
            <FontAwesomeIcon size='xl' icon='list' />
            <Typography variant='h6' color='primary'>
              {moduleTitle}
            </Typography>
          </Box>
          <Box
            style={{
              width: '100%',
              display: 'flex',
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}
          >
            <Grid container xs={8} item spacing={1} alignItems='flex-end' className='section-input m-0'>
              <Grid xs={7} item className='m-0'>
                <TextField
                  id='generate-assessment-title'
                  name='assessmentTitle'
                  value={title || ''}
                  error={error?.title ? true : false}
                  helperText={error?.title}
                  onChange={(e) => onChangeHeader('title', e.target.value)}
                  label='Title*'
                  InputProps={{ required: true }}
                  style={{ marginRight: '24px', width: '100%' }}
                  variant='standard'
                  className='custom-text-field'
                  disabled={!canUserEditTemplate}
                />
              </Grid>
              <Grid item xs={5} className='m-0'>
                {type === 'assessment' ? (
                  <MultiSelectDropdown
                    optionList={yearLevelsToRender ?? []}
                    limit={2}
                    value={selectedYearLevels ?? []}
                    onUpdateFilter={(newvalue) => {
                      const newYearLevels = newvalue?.yearLevels || [];

                      onChangeHeader('yearLevels', newYearLevels);
                      return;
                    }}
                    filterKey='yearLevels'
                    label='Year levels'
                    placeholder='Choose year levels'
                    open={open}
                    setOpen={setOpen}
                    error={error?.yearLevels ? true : false}
                    helperText={error?.yearLevels}
                  />
                ) : (
                  <MultiSelectDropdown
                    optionList={rolesToRender ?? []}
                    limit={2}
                    value={selectedRoles || []}
                    onUpdateFilter={(newvalue) => {
                      const newRoles = newvalue?.roles || [];

                      onChangeHeader('roles', newRoles);
                      return;
                    }}
                    filterKey='roles'
                    label='Roles'
                    placeholder='Choose roles'
                    open={open}
                    setOpen={setOpen}
                    error={error?.roles ? true : false}
                    helperText={error?.roles}
                  />
                )}
              </Grid>
            </Grid>

            <Box display={'flex'} gap={1}>
              {/* Only Maestro users can see this  action*/}
              {isMaestroUser && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isEditable}
                      onChange={(e) => onChangeHeader('isEditable', e.target.checked)}
                      size='large'
                      sx={{ padding: 0.5 }}
                    />
                  }
                  label={
                    <Typography
                      variant='body1'
                      color='text.secondary'
                      style={{
                        fontSize: '14px'
                      }}
                    >
                      Allow Editing
                    </Typography>
                  }
                />
              )}
              {permissions?.list && type === 'survey' && (
                <Button
                  size='small'
                  onClick={handleOnPreviewTemplate}
                  disabled={loadingRoles || loadingYears || loadingTemplates || loadingQuestions}
                  startIcon={<FontAwesomeIcon icon='expand-alt' />}
                  ref={saveRef}
                  variant='contained'
                >
                  Preview
                </Button>
              )}
              {permissions?.create && (
                <Button
                  size='small'
                  onClick={handleOnSaveTemplate}
                  disabled={loadingRoles || loadingYears || loadingTemplates || loadingQuestions}
                  startIcon={<FontAwesomeIcon icon='circle-check' />}
                  ref={saveRef}
                  variant='contained'
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>
        </Module.BodyHeader>
        <Module.BodyContainer className='overflow-y-auto'>
          <Box className='generator__main-container'>
            {sortedQuestions?.map((question, index) => {
              return (
                <Question
                  key={index}
                  index={qIndex++}
                  question={{ ...question }}
                  options={filteredTypes}
                  showDelete={sortedQuestions.length > 1 && canUserEditTemplate}
                  onChangeAlert={onChangeAlert}
                  templateType={templateType}
                  isLastQuestion={index === sortedQuestions.length - 1}
                  isEditable={canUserEditTemplate}
                />
              );
            })}
          </Box>
          <Box
            className={`generator__footer-container ${!canUserEditTemplate ? 'generator__footer-container--notice' : ''}`}
          >
            {canUserEditTemplate ? (
              <>
                <Box className='generator__footer-item'>
                  {permissions?.create && (
                    <Button
                      size='large'
                      onClick={handleOnDuplicateLastQuestion}
                      startIcon={<FontAwesomeIcon icon='copy' />}
                      className='custom-secondary__button'
                      disabled={!canUserEditTemplate}
                    >
                      Duplicate last Question
                    </Button>
                  )}
                </Box>
                <Box className='generator__footer-item'>
                  {permissions?.create && (
                    <Button
                      size='large'
                      onClick={handleAddNextQuestion}
                      startIcon={<FontAwesomeIcon icon='plus' />}
                      className='custom-primary__button'
                      disabled={!canUserEditTemplate}
                    >
                      Next Question
                    </Button>
                  )}
                </Box>
              </>
            ) : (
              <>
                <Box className='generator__footer-notice'>
                  <Typography
                    variant='body1'
                    color='text.secondary'
                    className='generator__footer-notice-text'
                  >
                    This template is not editable. Please contact your administrator for more information.
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Module.BodyContainer>
      </Module.Body>
      <SurveyTemplateDialog
        id={`template-builder-survey-modal`}
        open={showPreview}
        onCancel={() => setShowPreview(false)}
        onConfirm={() => setShowPreview(false)}
        loading={false}
        title={generator?.title ?? 'Template Preview'}
        onCloseHeader={true}
        maxWidth='lg'
        showActions={false}
        questions={sortedQuestions?.map((item) => {
          return {
            ...item,
            answer: [],
            error: null
          };
        })}
      />
    </Module>
  );
};
export default TemplateBuilder;
